import io, { Socket } from 'socket.io-client'
import { EventNames, EventParams } from 'socket.io/dist/typed-events'
import { toast } from 'react-hot-toast'
import {
	AdminMessageModel,
	PokerGameCommandModel,
	PokerGameModel,
	PokerPlayerCommandModel,
	User,
	UserDepositBonusModel,
	UserProfileModel,
	UserRankingsModel,
	UserStatisticsUpdateModel,
	UserWalletModel,
	WSChatMessage,
	WSFriendsGameInviteMessage,
	WSFriendsUpdateMessage,
	WSIdentifyMessage,
	WSPokerGameSetupRequest,
	WSStatsMessage,
	WSUpdateRequest,
} from 'mvppoker-portal-common/api'
import apiKeys from '../../config/apiKeys.json'
import { store } from '../../state/AppStore'
import { setConnectionStatus } from '../../state/reducers/websocketConnection'
import { WebsocketConnectionStatus } from './WebsocketConnectionStatus'
import { clearActiveUser } from '../../state/reducers/activeUser'
import ConsoleLogger from '../../utils/ConsoleLogger'

const wsHost = apiKeys.websockets

enum WebsocketMessageType {
	IDENTIFY = 'identify',
	IDENTIFY_KICK = 'identify-kick',
	TIME_SYNC = 'time-sync',
	STATS_USERSONLINE = 'stats-usersonline',
	COMMAND_FROMSERVER = 'game-command-from-server',
	COMMAND_PLAYER_TOSERVER = 'player-command-to-server',
	MESSAGE_GAMESETUPREQUESTTOSERVER = 'game-setup-request-to-server',
	MESSAGE_USERSTATSFROMSERVER = 'user-stats-from-server',

	MESSAGE_LOBBY_CHAT = 'lobby-chat',
	MESSAGE_LOBBY_CHAT_HISTORY = 'lobby-chat-history',
	MESSAGE_LOBBY_STATS = 'lobby-stats',

	MESSAGE_GAME_CHAT = 'game-chat',
	MESSAGE_FRIENDS_UPDATE = 'friends-update-from-server',
	MESSAGE_FRIENDS_CHAT = 'friends-chat',
	MESSAGE_FRIENDS_GAMEINVITE = 'friends-gameinvite',
	MESSAGE_ADMIN_MESSAGE = 'admin-message',

	MESSAGE_UPDATE_RANKINGS = 'update-rankings',
	MESSAGE_UPDATE_USER = 'update-user',
	MESSAGE_UPDATE_WALLET = 'update-wallet',
	MESSAGE_UPDATE_OTHERPROFILES = 'update-otherprofiles',
	MESSAGE_UPDATE_ROOMS = 'update-rooms',
	MESSAGE_UPDATE_DEPOSIT_BONUS = 'update-depositbonus',
}

type ServerToClient = {
	[WebsocketMessageType.IDENTIFY]: (msg: WSIdentifyMessage) => void
	[WebsocketMessageType.IDENTIFY_KICK]: (msg: string) => void
	[WebsocketMessageType.TIME_SYNC]: (time: number) => void
	[WebsocketMessageType.STATS_USERSONLINE]: (stats: WSStatsMessage) => void
	[WebsocketMessageType.COMMAND_FROMSERVER]: (command: PokerGameCommandModel) => void
	// [WebsocketMessageType.COMMAND_PLAYER_TOSERVER]: never
	// [WebsocketMessageType.MESSAGE_GAMESETUPREQUESTTOSERVER]: never
	[WebsocketMessageType.MESSAGE_USERSTATSFROMSERVER]: (stats: UserStatisticsUpdateModel) => void

	[WebsocketMessageType.MESSAGE_LOBBY_CHAT]: (msg: WSChatMessage) => void
	[WebsocketMessageType.MESSAGE_LOBBY_STATS]: (stats: WSStatsMessage) => void

	[WebsocketMessageType.MESSAGE_GAME_CHAT]: (msg: WSChatMessage) => void
	[WebsocketMessageType.MESSAGE_FRIENDS_UPDATE]: (msg: WSFriendsUpdateMessage) => void
	[WebsocketMessageType.MESSAGE_FRIENDS_CHAT]: (msg: WSChatMessage) => void
	[WebsocketMessageType.MESSAGE_FRIENDS_GAMEINVITE]: (msg: WSFriendsGameInviteMessage) => void
	[WebsocketMessageType.MESSAGE_ADMIN_MESSAGE]: (msg: AdminMessageModel) => void

}

type EmitRequests = {
	[WebsocketMessageType.MESSAGE_UPDATE_ROOMS]: (msg: WSUpdateRequest, callback: (rooms: PokerGameModel[]) => void) => void
	[WebsocketMessageType.MESSAGE_UPDATE_USER]: (msg: WSUpdateRequest, callback: (user: User) => void) => void
	[WebsocketMessageType.MESSAGE_UPDATE_RANKINGS]: (msg: WSUpdateRequest, callback: (rankings: UserRankingsModel) => void) => void
	[WebsocketMessageType.MESSAGE_UPDATE_WALLET]: (msg: WSUpdateRequest, callback: (rankings: UserWalletModel) => void) => void
	[WebsocketMessageType.MESSAGE_UPDATE_OTHERPROFILES]: (msg: WSUpdateRequest, callback: (users: UserProfileModel[]) => void) => void,
	[WebsocketMessageType.MESSAGE_UPDATE_DEPOSIT_BONUS]: (msg: WSUpdateRequest, callback: (bonus: UserDepositBonusModel | undefined) => void) => void,
	[WebsocketMessageType.MESSAGE_LOBBY_CHAT_HISTORY]: (msg: WSUpdateRequest, callback: (messages: WSChatMessage[]) => void) => void,
}

type EmitEvents = {
	[WebsocketMessageType.IDENTIFY]: (msg: WSIdentifyMessage) => void
	[WebsocketMessageType.MESSAGE_LOBBY_CHAT]: (msg: WSChatMessage) => void
	[WebsocketMessageType.MESSAGE_FRIENDS_GAMEINVITE]: (msg: WSFriendsGameInviteMessage) => void
	[WebsocketMessageType.COMMAND_PLAYER_TOSERVER]: (command: PokerPlayerCommandModel) => void
	[WebsocketMessageType.MESSAGE_GAMESETUPREQUESTTOSERVER]: (request: WSPokerGameSetupRequest) => void
	[WebsocketMessageType.MESSAGE_GAME_CHAT]: (msg: WSChatMessage) => void
	[WebsocketMessageType.MESSAGE_FRIENDS_CHAT]: (msg: WSChatMessage) => void
} & EmitRequests

type Unsubscribe = () => void

class WebsocketConnection
{
	static MAX_RECONNECTION_ATTEMPTS = 5

	private socket: Socket<ServerToClient, EmitEvents>

	private hasSetup = false

	private reconnectionAttempts = 0

	private userId?: string

	constructor()
	{
		this.socket = io(wsHost, {
			autoConnect: false,
			multiplex: false,
			forceNew: true,
			reconnection: true,
			randomizationFactor: 0.5,
			reconnectionDelay: 2_000,
			reconnectionDelayMax: 3_000,
			timeout: 12_000,
		})
	}

	public get connected() : boolean { return this.socket.connected }

	setup(userId: string)
	{
		ConsoleLogger.WS('Setup userId: ', userId)
		if (this.userId === userId && this.hasSetup) { return }

		this.userId = userId

		ConsoleLogger.WS('Setup callbacks')

		this.setupIdentifyKick()

		this.socket.on('connect', this.onConnected.bind(this))
		this.socket.on('disconnect', this.onDisconnected.bind(this))
		this.socket.on('connect_error', this.onConnectError.bind(this))

		// this.socket.on('reconnect', this.onReconnected)

		this.hasSetup = true
	}

	connect()
	{
		if (!this.hasSetup || this.connected) {
			return
		}

		this.socket.connect()
		ConsoleLogger.WS('perform connect')
	}

	disconnect()
	{
		if (!this.hasSetup) {
			return
		}

		this.socket.disconnect()
		this.hasSetup = false
	}

	setupIdentifyKick()
	{
		this.socket.on(WebsocketMessageType.IDENTIFY_KICK, () => {
			this.disconnect()
			store.dispatch(clearActiveUser())
			toast.error('Only one device session at a time allowed!')
		})
	}

	setupTimeSync() {
		// this.socket.on(WebsocketMessageType.TIME_SYNC, (time) => {
		//   // TODO
		// })
	}

	requestUserUpdate(callback: (user: User) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_USER, this.getTypedCallback(callback))
	}

	requestRoomsUpdate(callback: (rooms: PokerGameModel[]) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_ROOMS, this.getTypedCallback(callback))
	}

	requestWalletUpdate(callback: (wallet: UserWalletModel) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_WALLET, this.getTypedCallback(callback))
	}

	requestOtherProfilesUpdate(callback: (users: UserProfileModel[]) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_OTHERPROFILES, this.getTypedCallback(callback))
	}

	requestRankings(callback: (rankings: UserRankingsModel) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_RANKINGS, this.getTypedCallback(callback))
	}

	requestDepositBonusUpdate(callback: (bonus: UserDepositBonusModel | undefined) => void) {
		this.requestUpdate(WebsocketMessageType.MESSAGE_UPDATE_DEPOSIT_BONUS, this.getTypedCallback(callback))
	}

	requestUpdate<K extends keyof EmitRequests>(type: K, callback: Parameters<EmitRequests[K]>[1]) {
		const req: Parameters<EmitRequests[K]>[0] = { userId: this.userId!, role: 'USER' }
		const args = [req, callback] as Parameters<EmitEvents[K]>

		this.emit(type, ...args)
	}

	requestGameSetup(roomId: string)
	{
		this.emit(WebsocketMessageType.MESSAGE_GAMESETUPREQUESTTOSERVER, {
			userId: this.userId!,
			roomId,
			role: 'USER',
		})
	}

	sendPlayerCommand(command: PokerPlayerCommandModel)
	{
		this.emit(WebsocketMessageType.COMMAND_PLAYER_TOSERVER, command)
	}

	listenToCommands(callback: (command: PokerGameCommandModel) => void)
	{
		this.subscribe(WebsocketMessageType.COMMAND_FROMSERVER, this.getTypedCallback(callback))
	}

	stopListenToCommands()
	{
		this.unsubscribeAll(WebsocketMessageType.COMMAND_FROMSERVER)
	}

	listenToUserStats(callback: (update: UserStatisticsUpdateModel) => void)
	{
		this.subscribe(WebsocketMessageType.MESSAGE_USERSTATSFROMSERVER, this.getTypedCallback(callback))
	}

	stopListenToUserStats()
	{
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_USERSTATSFROMSERVER)
	}

	listenToFriends(callbackUpdate: (msg: WSFriendsUpdateMessage) => void, callbackChat: (msg: WSChatMessage) => void, callbackGameInvite: (msg: WSFriendsGameInviteMessage) => void)
	{
		this.subscribe(WebsocketMessageType.MESSAGE_FRIENDS_UPDATE, this.getTypedCallback(callbackUpdate))
		this.subscribe(WebsocketMessageType.MESSAGE_FRIENDS_CHAT, this.getTypedCallback(callbackChat))
		this.subscribe(WebsocketMessageType.MESSAGE_FRIENDS_GAMEINVITE, this.getTypedCallback(callbackGameInvite))
	}

	stopListenToFriends()
	{
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_FRIENDS_UPDATE)
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_FRIENDS_CHAT)
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_FRIENDS_GAMEINVITE)
	}

	sendFriendsChat(msg: WSChatMessage)
	{
		this.emit(WebsocketMessageType.MESSAGE_FRIENDS_CHAT, msg)
	}

	sendFriendsGameInvite(msg: WSFriendsGameInviteMessage)
	{
		this.emit(WebsocketMessageType.MESSAGE_FRIENDS_GAMEINVITE, msg)
	}

	sendGameChat(msg: WSChatMessage)
	{
		this.emit(WebsocketMessageType.MESSAGE_GAME_CHAT, msg)
	}

	listenToGameChat(callback: (msg: WSChatMessage) => void)
	{
		this.subscribe(WebsocketMessageType.MESSAGE_GAME_CHAT, this.getTypedCallback(callback))
	}

	stopListenToGameChat()
	{
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_GAME_CHAT)
	}

	listenToLobby(callbackChat: (msg: WSChatMessage) => void, callbackStats: (stats: WSStatsMessage) => void)
	{
		this.subscribe(WebsocketMessageType.MESSAGE_LOBBY_CHAT, this.getTypedCallback(callbackChat))
		this.subscribe(WebsocketMessageType.MESSAGE_LOBBY_STATS, this.getTypedCallback(callbackStats))
	}

	stopListenToLobby()
	{
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_LOBBY_CHAT)
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_LOBBY_STATS)
	}

	sendLobbyChat(msg: WSChatMessage)
	{
		this.emit(WebsocketMessageType.MESSAGE_LOBBY_CHAT, msg)
	}

	requestLobbyChatHistory(callback: (messages: WSChatMessage[]) => void)
	{
		this.requestUpdate(WebsocketMessageType.MESSAGE_LOBBY_CHAT_HISTORY, this.getTypedCallback(callback))
	}

    listenStatsUsersOnline(callback: (message: WSStatsMessage) => void)
    {
		this.subscribe(WebsocketMessageType.STATS_USERSONLINE, this.getTypedCallback(callback))
    }

	stopListenStatsUsersOnline()
	{
		this.unsubscribeAll(WebsocketMessageType.STATS_USERSONLINE)
	}

    listenAdminMessage(callback: (message: AdminMessageModel) => void)
    {
		this.subscribe(WebsocketMessageType.MESSAGE_ADMIN_MESSAGE, this.getTypedCallback(callback))
    }

	stopListenAdminMessage()
	{
		this.unsubscribeAll(WebsocketMessageType.MESSAGE_ADMIN_MESSAGE)
	}

	private getTypedCallback<T>(callback: (message: T) => void) : ((message: T) => void)
	{
		return (message: T) => {
			const payload = typeof message === 'string' ? JSON.parse(message) as T : message
			callback(payload)
		}
	}

	private subscribe<K extends keyof ServerToClient>(type: K, callback: ServerToClient[K]) : Unsubscribe
	{
		this.unsubscribeAll(type)
		this.socket.on(type, callback as any)

		return () => { this.socket.off(type, callback as any) }
	}
	
	private unsubscribeAll<K extends keyof ServerToClient>(type: K)
	{
		this.socket.removeAllListeners(type)
	}

	private onConnected()
	{
		if (!this.userId) {
			return
		}
		this.sendIdentify()
		this.reconnectionAttempts = 0
		store.dispatch(setConnectionStatus(WebsocketConnectionStatus.Connected))
		ConsoleLogger.WS('Connected')
	}

	private onDisconnected() {
		store.dispatch(setConnectionStatus(WebsocketConnectionStatus.Disconnected))
	}

	private onConnectError()
	{
		ConsoleLogger.WS('OnConnectError')
		this.reconnectionAttempts += 1
		store.dispatch(setConnectionStatus(WebsocketConnectionStatus.Disconnected))
	}

	private onReconnected()
	{
		ConsoleLogger.Lelele(`Websockets onReconnected: ${true}`)
		
	}

	// TODO: this gets sent twice
	private sendIdentify()
	{
		console.log('sendIdentify')
		if (!this.userId) {
			return
		}
		this.emit(WebsocketMessageType.IDENTIFY, { id: this.userId, role: 'USER' })
	}

	/// Bcz the BE expects a string instead of a JSON this fn converts args to string
	emit<Ev extends EventNames<EmitEvents>>(ev: Ev, ...args: EventParams<EmitEvents, Ev>) {
		const newArgs = args.map((it, idx) => {
			if (idx === args.length - 1 && typeof it === 'function') {
				return it
			}

			if (typeof it === 'object') {
				return JSON.stringify(it)
			}
			return it
		})
		this.socket.emit(ev, ...(newArgs as unknown as EventParams<EmitEvents, Ev>))
	}
}

export const websocketConnection = new WebsocketConnection()
